import React, { useState } from 'react';
import { Download, Layout, Palette, Type, Image, Sliders, Sparkles } from 'lucide-react';
import Button from '../common/Button';
import html2canvas from 'html2canvas';

interface BackgroundGeneratorProps {
  primaryColor?: string;
  onExport: (format: string, style: string) => void;
}

const BackgroundGenerator: React.FC<BackgroundGeneratorProps> = ({
  primaryColor = '#3B82F6',
  onExport
}) => {
  const [selectedFormat, setSelectedFormat] = useState('16:9');
  const [selectedStyle, setSelectedStyle] = useState('gradient');
  const [text, setText] = useState('');
  const [fontSize, setFontSize] = useState(48);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [textPosition, setTextPosition] = useState('center');
  const [showLogo, setShowLogo] = useState(true);
  const [logoSize, setLogoSize] = useState(64);
  const [logoPosition, setLogoPosition] = useState('top-left');
  const [gradientAngle, setGradientAngle] = useState(135);
  const [gradientStops, setGradientStops] = useState([
    { color: primaryColor, position: 0 },
    { color: adjustHue(primaryColor, 40), position: 50 },
    { color: adjustHue(primaryColor, -40), position: 100 }
  ]);
  const [patternSize, setPatternSize] = useState(50);
  const [patternOpacity, setPatternOpacity] = useState(0.2);
  const [backgroundBlur, setBackgroundBlur] = useState(0);
  const [animationEnabled, setAnimationEnabled] = useState(false);

  const formats = [
    { id: '16:9', label: 'Widescreen (16:9)', width: 1920, height: 1080 },
    { id: '4:3', label: 'Standard (4:3)', width: 1600, height: 1200 },
    { id: '21:9', label: 'Ultrawide (21:9)', width: 2560, height: 1080 },
    { id: 'square', label: 'Square (1:1)', width: 1500, height: 1500 },
    { id: 'mobile', label: 'Mobile Portrait (9:16)', width: 1080, height: 1920 },
    { id: 'tablet', label: 'Tablet (4:3)', width: 2048, height: 1536 },
    { id: 'social', label: 'Social Media', width: 1200, height: 630 },
    { id: 'banner', label: 'Website Banner', width: 1920, height: 400 }
  ];

  const styles = [
    { id: 'gradient', label: 'Gradient Mesh', icon: Palette },
    { id: 'waves', label: 'Wave Pattern', icon: Sliders },
    { id: 'dots', label: 'Dot Grid', icon: Image },
    { id: 'geometric', label: 'Geometric', icon: Layout },
    { id: 'sparkles', label: 'Sparkles', icon: Sparkles },
    { id: 'minimal', label: 'Minimal', icon: Type }
  ];

  const fonts = [
    'Arial',
    'Helvetica',
    'Georgia',
    'Times New Roman',
    'Verdana',
    'Roboto',
    'Open Sans',
    'Montserrat',
    'Playfair Display',
    'Poppins'
  ];

  const positions = [
    { id: 'top-left', label: 'Top Left' },
    { id: 'top-center', label: 'Top Center' },
    { id: 'top-right', label: 'Top Right' },
    { id: 'center-left', label: 'Center Left' },
    { id: 'center', label: 'Center' },
    { id: 'center-right', label: 'Center Right' },
    { id: 'bottom-left', label: 'Bottom Left' },
    { id: 'bottom-center', label: 'Bottom Center' },
    { id: 'bottom-right', label: 'Bottom Right' }
  ];

  const getPositionStyles = (position: string) => {
    const baseStyles = 'absolute transform';
    const positionMap: Record<string, string> = {
      'top-left': 'top-4 left-4',
      'top-center': 'top-4 left-1/2 -translate-x-1/2',
      'top-right': 'top-4 right-4',
      'center-left': 'top-1/2 left-4 -translate-y-1/2',
      'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
      'center-right': 'top-1/2 right-4 -translate-y-1/2',
      'bottom-left': 'bottom-4 left-4',
      'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
      'bottom-right': 'bottom-4 right-4'
    };
    return `${baseStyles} ${positionMap[position]}`;
  };

  const generateGradient = () => {
    const stops = gradientStops.map(stop => 
      `${stop.color} ${stop.position}%`
    ).join(', ');
    return `linear-gradient(${gradientAngle}deg, ${stops})`;
  };

  const generatePattern = () => {
    switch (selectedStyle) {
      case 'waves':
        return `
          repeating-linear-gradient(
            ${gradientAngle}deg,
            transparent,
            transparent ${patternSize/2}px,
            rgba(255,255,255,${patternOpacity}) ${patternSize/2}px,
            rgba(255,255,255,${patternOpacity}) ${patternSize}px
          )
        `;
      case 'dots':
        return `
          radial-gradient(
            circle at ${patternSize/2}px ${patternSize/2}px,
            rgba(255,255,255,${patternOpacity}) 2px,
            transparent 0
          )
        `;
      case 'geometric':
        return `
          repeating-linear-gradient(
            ${gradientAngle}deg,
            transparent,
            transparent ${patternSize}px,
            rgba(255,255,255,${patternOpacity}) ${patternSize}px,
            rgba(255,255,255,${patternOpacity}) ${patternSize * 2}px
          ),
          repeating-linear-gradient(
            ${gradientAngle + 60}deg,
            transparent,
            transparent ${patternSize}px,
            rgba(255,255,255,${patternOpacity}) ${patternSize}px,
            rgba(255,255,255,${patternOpacity}) ${patternSize * 2}px
          )
        `;
      case 'sparkles':
        return `
          radial-gradient(
            circle at ${Math.random() * 100}% ${Math.random() * 100}%,
            rgba(255,255,255,${patternOpacity}) 1px,
            transparent 1px
          )
        `;
      default:
        return '';
    }
  };

  const generateBackground = () => {
    const gradient = generateGradient();
    const pattern = generatePattern();
    const blur = backgroundBlur ? `blur(${backgroundBlur}px)` : '';
    
    let background = gradient;
    if (pattern) {
      background = `${gradient}, ${pattern}`;
    }

    const animation = animationEnabled ? 'animate-gradient' : '';
    
    return {
      background,
      backgroundSize: `100% 100%, ${patternSize * 2}px ${patternSize * 2}px`,
      filter: blur,
      animation
    };
  };

  const handleExport = async (format: 'png' | 'svg') => {
    const element = document.querySelector('.background-preview');
    if (!element) return;

    try {
      if (format === 'svg') {
        // Generate SVG background
        const svgContent = generateBackgroundSVG();
        const blob = new Blob([svgContent], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `scalenix-background-${selectedStyle}.svg`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        // Export PNG using html2canvas
        const canvas = await html2canvas(element as HTMLElement);
        const link = document.createElement('a');
        link.download = `scalenix-background-${selectedStyle}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    } catch (error) {
      console.error('Failed to export background:', error);
    }
  };

  const generateBackgroundSVG = () => {
    const format = formats.find(f => f.id === selectedFormat);
    const width = format?.width || 1920;
    const height = format?.height || 1080;

    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            ${gradientStops.map(stop => 
              `<stop offset="${stop.position}%" style="stop-color:${stop.color};stop-opacity:1" />`
            ).join('')}
          </linearGradient>
          ${selectedStyle !== 'minimal' ? `
            <pattern id="pattern" width="${patternSize}" height="${patternSize}" patternUnits="userSpaceOnUse">
              ${generatePatternSVG()}
            </pattern>
          ` : ''}
        </defs>
        <rect width="100%" height="100%" fill="url(#gradient)" />
        ${selectedStyle !== 'minimal' ? `
          <rect width="100%" height="100%" fill="url(#pattern)" opacity="${patternOpacity}" />
        ` : ''}
        ${text ? `
          <text
            x="50%"
            y="50%"
            font-family="${fontFamily}"
            font-size="${fontSize}"
            fill="${textColor}"
            text-anchor="middle"
            dominant-baseline="middle"
          >${text}</text>
        ` : ''}
      </svg>
    `;
  };

  const generatePatternSVG = () => {
    switch (selectedStyle) {
      case 'dots':
        return `
          <circle cx="${patternSize/2}" cy="${patternSize/2}" r="2" fill="#fff" />
        `;
      case 'waves':
        return `
          <path d="M0 ${patternSize/2} Q ${patternSize/4} ${patternSize/4}, ${patternSize/2} ${patternSize/2} T ${patternSize} ${patternSize/2}" 
                fill="none" stroke="#fff" stroke-width="2" />
        `;
      case 'geometric':
        return `
          <path d="M0 0 L${patternSize} ${patternSize} M0 ${patternSize} L${patternSize} 0" 
                stroke="#fff" stroke-width="2" />
        `;
      default:
        return '';
    }
  };

  return (
    <div className="mt-8 border-t pt-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Background Generator
      </h3>

      <div className="space-y-6">
        {/* Format Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Format
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {formats.map((format) => (
              <button
                key={format.id}
                onClick={() => setSelectedFormat(format.id)}
                className={`flex items-center justify-center px-4 py-2 border rounded-lg text-sm font-medium transition-colors
                  ${selectedFormat === format.id
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <Layout className="h-4 w-4 mr-2" />
                {format.label}
              </button>
            ))}
          </div>
        </div>

        {/* Style Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Style
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {styles.map((style) => {
              const Icon = style.icon;
              return (
                <button
                  key={style.id}
                  onClick={() => setSelectedStyle(style.id)}
                  className={`flex items-center justify-center px-4 py-2 border rounded-lg text-sm font-medium transition-colors
                    ${selectedStyle === style.id
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {style.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Gradient Controls */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Gradient Settings
          </label>
          
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Angle</label>
              <input
                type="range"
                min="0"
                max="360"
                value={gradientAngle}
                onChange={(e) => setGradientAngle(parseInt(e.target.value))}
                className="w-32"
              />
              <span className="ml-2 text-sm text-gray-600">{gradientAngle}°</span>
            </div>
            
            {gradientStops.map((stop, index) => (
              <div key={index}>
                <label className="block text-xs text-gray-500 mb-1">
                  Color {index + 1}
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={stop.color}
                    onChange={(e) => {
                      const newStops = [...gradientStops];
                      newStops[index].color = e.target.value;
                      setGradientStops(newStops);
                    }}
                    className="h-8 w-8 rounded border border-gray-300"
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={stop.position}
                    onChange={(e) => {
                      const newStops = [...gradientStops];
                      newStops[index].position = parseInt(e.target.value);
                      setGradientStops(newStops);
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-600">%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pattern Controls */}
        {selectedStyle !== 'minimal' && (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Pattern Settings
            </label>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Size</label>
                <input
                  type="range"
                  min="10"
                  max="200"
                  value={patternSize}
                  onChange={(e) => setPatternSize(parseInt(e.target.value))}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">{patternSize}px</span>
              </div>
              
              <div>
                <label className="block text-xs text-gray-500 mb-1">Opacity</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={patternOpacity * 100}
                  onChange={(e) => setPatternOpacity(parseInt(e.target.value) / 100)}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">{Math.round(patternOpacity * 100)}%</span>
              </div>
              
              <div>
                <label className="block text-xs text-gray-500 mb-1">Blur</label>
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={backgroundBlur}
                  onChange={(e) => setBackgroundBlur(parseInt(e.target.value))}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">{backgroundBlur}px</span>
              </div>
            </div>
          </div>
        )}

        {/* Text Options */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Text
            </label>
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Enter text to display"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Font Family
              </label>
              <select
                value={fontFamily}
                onChange={(e) => setFontFamily(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                {fonts.map((font) => (
                  <option key={font} value={font}>{font}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Font Size
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="range"
                  min="12"
                  max="120"
                  value={fontSize}
                  onChange={(e) => setFontSize(parseInt(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm text-gray-600 w-12">{fontSize}px</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text Color
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  className="h-8 w-8 rounded border border-gray-300"
                />
                <input
                  type="text"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  className="flex-1 px-3 py-1 border border-gray-300 rounded-md"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Text Position
            </label>
            <div className="grid grid-cols-3 gap-2">
              {positions.map((pos) => (
                <button
                  key={pos.id}
                  onClick={() => setTextPosition(pos.id)}
                  className={`px-3 py-2 text-sm border rounded-md transition-colors
                    ${textPosition === pos.id
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  {pos.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Logo Options */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="show-logo"
              checked={showLogo}
              onChange={(e) => setShowLogo(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="show-logo" className="text-sm font-medium text-gray-700">
              Show Logo
            </label>
          </div>

          {showLogo && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo Size
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="32"
                    max="256"
                    value={logoSize}
                    onChange={(e) => setLogoSize(parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-sm text-gray-600 w-12">{logoSize}px</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo Position
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {positions.map((pos) => (
                    <button
                      key={pos.id}
                      onClick={() => setLogoPosition(pos.id)}
                      className={`px-3 py-2 text-sm border rounded-md transition-colors
                        ${logoPosition === pos.id
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      {pos.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Animation Toggle */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="enable-animation"
            checked={animationEnabled}
            onChange={(e) => setAnimationEnabled(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="enable-animation" className="text-sm font-medium text-gray-700">
            Enable Animation
          </label>
        </div>

        {/* Preview */}
        <div className="relative overflow-hidden rounded-lg border border-gray-200">
          <div 
            className="background-preview aspect-video w-full relative"
            style={generateBackground()}
          >
            {showLogo && (
              <div className={getPositionStyles(logoPosition)}>
                <div 
                  style={{ 
                    width: logoSize,
                    height: logoSize
                  }}
                >
                  {/* Logo SVG */}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-white">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            )}
            
            {text && (
              <div 
                className={getPositionStyles(textPosition)}
                style={{
                  fontFamily,
                  fontSize: `${fontSize}px`,
                  color: textColor
                }}
              >
                {text}
              </div>
            )}
          </div>
        </div>

        {/* Export Options */}
        <div className="flex justify-end space-x-4">
          <Button
            variant="secondary"
            onClick={() => handleExport('png')}
            icon={Download}
          >
            Export PNG
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleExport('svg')}
            icon={Download}
          >
            Export SVG
          </Button>
        </div>
      </div>
    </div>
  );
};

// Helper function to adjust color hue
const adjustHue = (color: string, amount: number): string => {
  const hex = color.replace('#', '');
  const rgb = parseInt(hex, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;

  const hsl = rgbToHsl(r, g, b);
  hsl[0] = (hsl[0] + amount) % 360;
  const [nr, ng, nb] = hslToRgb(hsl[0], hsl[1], hsl[2]);

  return '#' + ((1 << 24) + (nr << 16) + (ng << 8) + nb).toString(16).slice(1);
};

// Helper function to convert RGB to HSL
const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }

    h *= 60;
  }

  return [h, s, l];
};

// Helper function to convert HSL to RGB
const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, (h / 360) + 1/3);
    g = hue2rgb(p, q, h / 360);
    b = hue2rgb(p, q, (h / 360) - 1/3);
  }

  return [
    Math.round(r * 255),
    Math.round(g * 255),
    Math.round(b * 255)
  ];
};

export default BackgroundGenerator;